import { Observable } from 'rxjs';
import { useRouter } from 'vue-router';

import { useFetchClient, useSession } from '../composables';
import { authForgotPasswordUrl, authResetPasswordUrl, authRevokeUrl, authTokenUrl } from '../urls';

export interface OAuthResponse {
	access_token: string;
	expires_in: number;
	refresh_token: string;
	created_at: string;
};

export const useAuthLogin = (): { authLogin: (email: string, password: string) => Observable<OAuthResponse> } => {
    const router = useRouter();
    const { post } = useFetchClient();
    const { setSession } = useSession();

    const authLogin = (email: string, password: string): Observable<OAuthResponse> => {
        return new Observable(subscriber => {
            post<OAuthResponse>(authTokenUrl(), {
                grant_type: 'password',
                username: email,
                password: password,
                client_id: import.meta.env.VITE_API_CLIENT_ID
            }).then(data => {
                setSession(data);
                if (router.currentRoute.value.query.redirect) {
                    router.push(router.currentRoute.value.query.redirect as string);
                }
                else {
                    router.push({
                        name: 'root'
                    });
                }
                subscriber.complete();
            }).catch(error => {
                subscriber.error(error);
            });
        });
    };

    return {
        authLogin
    };
};

export const useAuthLogout = (): { authLogout: () => Observable<void> } => {
    const router = useRouter();
    const { post } = useFetchClient();
    const { accessToken, clearSession } = useSession();

    const authLogout = (): Observable<void> => {
        return new Observable(subscriber => {
            post(authRevokeUrl(), {
                access_token: accessToken.value,
                client_id: import.meta.env.VITE_API_CLIENT_ID
            }).then(() => {
                clearSession();
                router.push({
                    name: 'login'
                });
                subscriber.complete();
            }).catch(error => {
                subscriber.error(error);
            });
        });
    };

    return {
        authLogout
    };
};

export const useAuthRefresh = (): { authRefresh: () => Promise<OAuthResponse> } => {
    const { post } = useFetchClient();
    const { refreshToken } = useSession();

    const authRefresh = (): Promise<OAuthResponse> => {
        return post<OAuthResponse>(authTokenUrl(), {
            grant_type: 'refresh_token',
            refresh_token: refreshToken.value,
            client_id: import.meta.env.VITE_API_CLIENT_ID
        });
    };

    return {
        authRefresh
    };
};

export const useAuthForgotPassword = (): { forgotPassword: (email: string) => Promise<void> } => {
    const { post } = useFetchClient();

    const forgotPassword = (email: string): Promise<void> => {
        return post(authForgotPasswordUrl(), {
            email: email
        });
    };

    return {
        forgotPassword
    };
};

export const useAuthResetPassword = (): { resetPassword: (token: string, newPassword: string) => Promise<void> } => {
    const { post } = useFetchClient();

    const resetPassword = (token: string, newPassword: string): Promise<void> => {
        return post(authResetPasswordUrl(), {
            token: token,
            new_password: newPassword
        });
    };

    return {
        resetPassword
    };
};